import { useState, useCallback } from 'react';

/**
 * sessionStorageを使用した状態管理フック
 * @param key - sessionStorageのキー
 * @param defaultValue - デフォルト値
 * @returns [value, setValue] - 状態値と更新関数
 */
export function useSessionStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T) => void] {
  // 初期値をsessionStorageから取得
  const getStoredValue = (): T => {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [value, setValue] = useState<T>(getStoredValue);

  // 値を更新し、sessionStorageにも保存
  const setStoredValue = useCallback((newValue: T) => {
    setValue(newValue);
    try {
      sessionStorage.setItem(key, JSON.stringify(newValue));
    } catch {
      // sessionStorage書き込みエラーは無視
    }
  }, [key]);

  return [value, setStoredValue];
}

/**
 * sessionStorageへの保存のみを行うユーティリティ（useEffectと組み合わせて使用）
 * @param key - sessionStorageのキー
 * @param value - 保存する値
 */
export const saveToSessionStorage = <T>(key: string, value: T): void => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage書き込みエラーは無視
  }
};

/**
 * sessionStorageから値を取得するユーティリティ
 * @param key - sessionStorageのキー
 * @param defaultValue - デフォルト値
 * @returns 取得した値またはデフォルト値
 */
export const getFromSessionStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};
